import React from 'react';
import {
  getLocationOptions,
  normalizeRegistrationSurveyAnswers,
  REGISTRATION_SURVEY_QUESTIONS
} from '../utils/registrationSurveyModel';

function cardClass(selected, readOnly, compact = false, darkSurface = false) {
  return [
    'relative border transition-all',
    compact ? 'min-h-[60px] rounded-2xl px-3 py-3 sm:min-h-[66px] sm:px-3.5' : 'min-h-[128px] rounded-[22px] px-5 py-6',
    selected
      ? `border-[#f28b82] bg-[#fff5f4] shadow-[0_10px_28px_rgba(242,139,130,0.18)] ${darkSurface ? 'dark:border-[#ef8c84] dark:bg-[#3a2022]' : ''}`
      : `border-[#dfe4ec] bg-white ${darkSurface ? 'dark:border-[#30363f] dark:bg-[#14181f]' : ''}`,
    readOnly ? 'cursor-default' : 'cursor-pointer hover:border-[#f4b0aa] hover:shadow-[0_8px_24px_rgba(148,163,184,0.10)]'
  ].join(' ');
}

function choiceLabel(option) {
  return typeof option === 'string' ? option : option?.label || '';
}

export default function RegistrationSurveyFields({
  answers,
  onChange,
  readOnly = false,
  className = '',
  density = 'default',
  tone = 'default',
  helperText = 'Bu saatleri daha sonra profilinden değiştirebilirsin.'
}) {
  const normalized = normalizeRegistrationSurveyAnswers({ answers });
  const { cities, districts } = getLocationOptions(normalized.city);
  const isCompact = density === 'compact';
  const isDarkSurface = tone === 'surface-dark';

  const setNext = (updater) => {
    if (readOnly || typeof onChange !== 'function') return;
    const next = typeof updater === 'function' ? updater(normalized) : updater;
    onChange(next);
  };

  const updateSimple = (key, value) => setNext((prev) => ({ ...prev, [key]: value }));

  const toggleCheckbox = (key, value) => {
    setNext((prev) => {
      const current = Array.isArray(prev[key]) ? prev[key] : [];
      if (key === 'q5' && value === 'Yok') {
        const nextValues = current.includes('Yok') ? [] : ['Yok'];
        return { ...prev, [key]: nextValues };
      }
      const cleanCurrent = key === 'q5' ? current.filter((item) => item !== 'Yok') : current;
      const set = new Set(cleanCurrent);
      if (set.has(value)) set.delete(value);
      else set.add(value);
      return { ...prev, [key]: Array.from(set) };
    });
  };

  const updateMeal = (slot, patch) => {
    setNext((prev) => ({
      ...prev,
      q8: {
        ...prev.q8,
        [slot]: {
          ...prev.q8[slot],
          ...patch
        }
      }
    }));
  };

  const sectionGapClass = isCompact ? 'space-y-3.5' : 'space-y-5';
  const headingGapClass = isCompact ? 'gap-2' : 'gap-3';
  const badgeClass = isCompact ? 'h-5 w-5 text-[10px]' : 'h-7 w-7 text-sm';
  const headingClass = isCompact ? 'text-base leading-snug sm:text-[19px]' : 'text-[28px]';
  const headingTextClass = isDarkSurface ? 'text-[#111827] dark:text-[#f3f4f6]' : 'text-[#111827]';
  const labelTextClass = isDarkSurface ? 'text-[#475569] dark:text-[#cbd5e1]' : 'text-[#475569]';
  const mutedTextClass = isDarkSurface ? 'text-[#94a3b8] dark:text-[#94a3b8]' : 'text-[#94a3b8]';
  const mealCardClass = isDarkSurface ? 'dark:border-[#30363f] dark:bg-[#14181f]' : '';
  const mealTitleClass = isDarkSurface ? 'text-[#334155] dark:text-[#e5e7eb]' : 'text-[#334155]';
  const mealToggleTextClass = isDarkSurface ? 'text-[#6b7280] dark:text-[#a8b3c7]' : 'text-[#6b7280]';
  const timeRowClass = isDarkSurface ? 'dark:border-[#2a3039] dark:bg-[#0f1318]' : '';
  const timeTextClass = isDarkSurface ? 'text-[#111827] dark:text-[#f8fafc] disabled:text-[#475569] dark:disabled:text-[#94a3b8]' : 'text-[#111827] disabled:text-[#475569]';
  const iconClass = isDarkSurface ? 'text-[#94a3b8] dark:text-[#7f8da3]' : 'text-[#94a3b8]';
  const alarmClass = isDarkSurface ? 'text-[#111827] dark:text-[#e5e7eb]' : 'text-[#111827]';
  const selectClassBase = isDarkSurface
    ? 'border-[#dfe4ec] bg-white text-[#111827] dark:border-[#30363f] dark:bg-[#14181f] dark:text-[#f3f4f6]'
    : 'border-[#dfe4ec] bg-white text-[#111827]';

  return (
    <div className={`${isCompact ? 'space-y-6' : 'space-y-10'} ${className}`}>
      {REGISTRATION_SURVEY_QUESTIONS.map((question) => {
        if (question.type === 'meal_preferences') {
          return (
            <section key={question.id} className={sectionGapClass}>
              <div className={`flex items-center ${headingGapClass}`}>
                <span className={`flex items-center justify-center rounded-full bg-black font-bold text-white ${badgeClass}`}>
                  {question.number}
                </span>
                <h3 className={`${headingClass} ${headingTextClass} font-semibold tracking-[-0.02em]`}>
                  {question.label}
                </h3>
              </div>
              <div className={`grid grid-cols-1 ${isCompact ? 'gap-2.5 xl:grid-cols-3' : 'gap-4 md:grid-cols-3'}`}>
                {['breakfast', 'lunch', 'dinner'].map((slot) => {
                  const item = normalized.q8[slot];
                  const title = item.label || '';

                  return (
                    <div
                      key={slot}
                      className={`border border-[#dfe4ec] bg-white shadow-sm ${mealCardClass} ${isCompact ? 'rounded-2xl px-3.5 py-3.5' : 'rounded-[24px] px-6 py-6'}`}
                    >
                      <div className={`text-center font-semibold ${mealTitleClass} ${isCompact ? 'mb-2 text-base leading-tight sm:text-lg' : 'mb-5 text-[28px]'}`}>
                        {title}
                      </div>
                      <label
                        className={`flex items-center justify-center gap-2 ${mealToggleTextClass} ${readOnly ? 'cursor-default' : 'cursor-pointer'} ${isCompact ? 'mb-2.5 text-xs sm:text-[13px]' : 'mb-5 text-[18px]'}`}
                      >
                        <input
                          type="checkbox"
                          className={`${isCompact ? 'h-3.5 w-3.5' : 'h-5 w-5'} rounded border-[#cdd5df] text-[#f28b82] focus:ring-[#f28b82] dark:border-[#485464]`}
                          checked={!item.enabled}
                          disabled={readOnly}
                          onChange={() => updateMeal(slot, { enabled: !item.enabled })}
                        />
                        Yapmıyorum
                      </label>
                      <div
                        className={`flex items-center justify-between border border-[#eef2f6] bg-[#f8fafc] ${timeRowClass} ${isCompact ? 'rounded-xl px-2.5 py-2' : 'rounded-2xl px-5 py-4'}`}
                      >
                        <span className={`material-symbols-outlined ${iconClass} ${isCompact ? 'text-[16px]' : 'text-[20px]'}`}>
                          schedule
                        </span>
                        <input
                          type="time"
                          value={item.time || ''}
                          disabled={readOnly}
                          onChange={(e) => updateMeal(slot, { time: e.target.value })}
                          className={`w-full bg-transparent text-center font-semibold tracking-[0.03em] outline-none ${timeTextClass} ${isCompact ? 'text-[15px] sm:text-base' : 'text-[24px]'}`}
                        />
                        <span className={`material-symbols-outlined ${alarmClass} ${isCompact ? 'text-[16px]' : 'text-[20px]'}`}>
                          alarm
                        </span>
                      </div>
                    </div>
                  );
                })}
              </div>
              {helperText ? (
                <p className={`${isCompact ? 'text-[11px]' : 'text-sm'} ${mutedTextClass}`}>
                  {helperText}
                </p>
              ) : null}
            </section>
          );
        }

        const value = normalized[question.id];

        return (
          <section key={question.id} className={sectionGapClass}>
            <div className={`flex items-center ${headingGapClass}`}>
              <span className={`flex items-center justify-center rounded-full bg-black font-bold text-white ${badgeClass}`}>
                {question.number}
              </span>
              <h3 className={`${headingClass} ${headingTextClass} font-semibold tracking-[-0.02em]`}>
                {question.label}
              </h3>
            </div>
            <div className={`grid ${isCompact ? 'gap-2.5' : 'gap-4'} ${question.columns || 'grid-cols-1'}`}>
              {(question.options || []).map((option) => {
                const label = choiceLabel(option);
                const checked = question.type === 'checkbox'
                  ? Array.isArray(value) && value.includes(label)
                  : value === label;

                return (
                  <button
                    key={label}
                    type="button"
                    disabled={readOnly}
                    onClick={() => {
                      if (question.type === 'checkbox') toggleCheckbox(question.id, label);
                      else updateSimple(question.id, label);
                    }}
                    className={cardClass(checked, readOnly, isCompact || question.compact, isDarkSurface)}
                  >
                    <div className={`flex h-full flex-col items-center justify-center text-center ${isCompact ? 'gap-2' : 'gap-4'}`}>
                      <span className={`${isCompact ? 'text-[13px] leading-snug sm:text-sm' : 'text-[18px]'} ${labelTextClass} font-semibold`}>
                        {label}
                      </span>
                      {question.type === 'checkbox' ? (
                        <span className={`${isCompact ? 'h-3.5 w-3.5' : 'h-5 w-5'} rounded border ${checked ? 'border-[#f28b82] bg-[#f28b82]' : 'border-[#cbd5e1] bg-white dark:border-[#485464] dark:bg-transparent'}`} />
                      ) : null}
                    </div>
                  </button>
                );
              })}
            </div>
            {question.id === 'q7' && String(normalized.q7 || '').trim() === 'Evet' ? (
              <div className={`grid grid-cols-1 ${isCompact ? 'gap-2.5 md:grid-cols-2' : 'gap-4 md:grid-cols-2'}`}>
                <label className="block">
                  <span className={`mb-2 block text-sm font-medium ${labelTextClass}`}>İl</span>
                  <select
                    value={normalized.city}
                    disabled={readOnly}
                    onChange={(e) => setNext((prev) => ({ ...prev, city: e.target.value, district: '' }))}
                    className={`w-full rounded-2xl border font-medium outline-none focus:border-[#f28b82] ${selectClassBase} ${isCompact ? 'px-3.5 py-2.5 text-[13px]' : 'px-4 py-4 text-base'}`}
                  >
                    <option value="">İl seçiniz...</option>
                    {cities.map((city) => <option key={city} value={city}>{city}</option>)}
                  </select>
                </label>
                <label className="block">
                  <span className={`mb-2 block text-sm font-medium ${labelTextClass}`}>İlçe</span>
                  <select
                    value={normalized.district}
                    disabled={readOnly || !normalized.city}
                    onChange={(e) => updateSimple('district', e.target.value)}
                    className={`w-full rounded-2xl border font-medium outline-none focus:border-[#f28b82] disabled:cursor-not-allowed disabled:opacity-60 ${selectClassBase} ${isCompact ? 'px-3.5 py-2.5 text-[13px]' : 'px-4 py-4 text-base'}`}
                  >
                    <option value="">{normalized.city ? 'İlçe seçiniz...' : 'Önce il seçiniz'}</option>
                    {districts.map((district) => <option key={district} value={district}>{district}</option>)}
                  </select>
                </label>
              </div>
            ) : null}
          </section>
        );
      })}
    </div>
  );
}
